import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import BrokersAddBrokerDialogAddressPage from "../Pages/BrokersAddBrokerDialogAddressPage";
import BrokersAddBrokerDialogContactPage from "../Pages/BrokersAddBrokerDialogContactPage";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import type { AddBrokerBrokerType } from "@/Shared/Types/brokerTypes";

interface BrokersAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFinished: (newSavedBroker: SavedBroker) => void;
}

// Two-page add-broker wizard (address -> contact), hosted in a ui-core
// Dialog. The pages render their own Cancel/Next/Previous/Save buttons, so
// the dialog supplies no footer. Remount with a fresh `key` per open to
// reset the wizard state.
export function BrokersAddDialog(props: BrokersAddDialogProps) {
  const [broker, setBroker] = useState<AddBrokerBrokerType>({
    businessName: undefined,
    accountNumber: undefined,
    address: {},
    contact: {},
  });

  const [currentPage, setCurrentPage] = useState<"address" | "contact">(
    "address",
  );

  function onDone() {
    props.onFinished({
      savedBrokerId: uuidv4(),
      broker: broker as Broker,
    });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Add Customs Broker"
      // Cargo sized this form to a 520px address field, and its City /
      // State / Postal row adds up to exactly that. `narrow` leaves ~448px
      // of content, which squeezed every field.
      width="default"
    >
      {/* Dashboard is a customer app and Cargo drew this wizard with underlined
          fields. A dialog inherits whatever the screen behind it declared, and
          the brokers screen declares nothing, so without this the fields come
          out in the dense boxed admin style. */}
      <ControlPresentationProvider presentation="underlined">
        {currentPage === "address" && (
          <BrokersAddBrokerDialogAddressPage
            setBroker={setBroker}
            broker={broker}
            onCancel={() => props.onOpenChange(false)}
            onSwitchToContactPage={() => setCurrentPage("contact")}
          />
        )}

        {currentPage === "contact" && (
          <BrokersAddBrokerDialogContactPage
            contact={broker.contact}
            contactChanged={function (updatedContact: AddContactContactType) {
              setBroker({
                ...broker,
                contact: {
                  ...broker.contact,
                  ...updatedContact,
                },
              });
            }}
            onPrevious={() => setCurrentPage("address")}
            onDone={onDone}
          />
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
